import React, { useEffect, useState } from 'react'
import { Col, Row, FormGroup, Alert, Input } from 'reactstrap'
import { Controller, ErrorMessage, useFormContext } from 'react-hook-form'
import Switch from 'react-switch'
import { useFormUtils } from 'views/shared/useFormUtils'
import PropTypes from 'prop-types'
import { isNotNumericDecimal } from 'context/Utilities'

const PlantMilkNutritionReview = ({ item, isDisabled }) => {
  const [packaging, setPackaging] = useState({ ...item })
  const methods = useFormContext()
  const { setSwitchReviewer, onChangeReviewer, asterisk } = useFormUtils()

  useEffect(() => {
    if (item !== null) {
      setPackaging({ ...item })
    }
  }, [item])

  const renderField = (
    name,
    label,
    maxLength,
    min,
    placeholder,
    message,
    max,
    maxMessage
  ) => (
    <FormGroup>
      <p>
        <strong>
          {label} {asterisk}
        </strong>
      </p>
      <Controller
        as={<Input />}
        name={name}
        type="number"
        step="0.01"
        defaultValue={packaging[name]}
        placeholder={placeholder}
        control={methods.control}
        rules={{
          required: `⚠ Please enter ${label}.`,
          maxLength: {
            value: maxLength,
            message: `⚠ ${label} exceeds ${maxLength} characters.`,
          },
          min: {
            value: min,
            message,
          },
          max: max !== null ? { value: max, message: maxMessage } : undefined,
          pattern: {
            value: /^(\d+(\.\d{1,2})?|\.\d{1,2})$/,
            message: '⚠ Maximum two decimal places allowed.',
          },
        }}
        onKeyDown={e => isNotNumericDecimal(e.key) && e.preventDefault()}
        onChange={([event]) => {
          onChangeReviewer(item, event)
          return event
        }}
        disabled={isDisabled}
      />
      <ErrorMessage
        errors={methods.errors}
        name={name}
        as={<Alert color="danger" className="mt-3" />}
      />
    </FormGroup>
  )

  const renderSwitch = (name, label, errorMessage) => (
    <FormGroup>
      <p>
        <strong>
          {label} {asterisk}
        </strong>
      </p>
      <Controller
        as={Switch}
        name={name}
        id={name}
        defaultValue={packaging[name]}
        onChange={([checked, event, id]) => {
          setSwitchReviewer(item, checked, event, id)
          return checked
        }}
        control={methods.control}
        rules={{ validate: value => value === true }}
        disabled={isDisabled}
      />
      <ErrorMessage
        errors={methods.errors}
        name={name}
        message={errorMessage}
        as={<Alert color="danger" className="mt-3" />}
      />
    </FormGroup>
  )

  return (
    <>
      {[
        // [field, label, maxLength, min, placeholder, errorMessage]
        [
          'Sugar',
          'Added Sugar (g) per 8 ounces',
          3,
          0,
          'Max 10g added sugar per 8 ounces',
          '⚠ Added Sugar (g) per 8 ounces exceeds the 10g maximum allowed.',
          'I certify that this product contains no more than 10g added sugar per 8 ounces.',
          '⚠ You must certify that this product contains no more than the maximum 10g total added sugar allowed per 8 ounces.',
          10,
          '⚠ Added Sugar (g) per 8 ounces exceeds the 10g maximum allowed.',
        ],
        [
          'Calcium',
          'Calcium (mg) per 8 ounces',
          6,
          276,
          'Min 276mg calcium per 8 ounces',
          '⚠ Calcium (mg) per 8 ounces is below the required 276mg minimum.',
          'I certify that this product contains at least 276mg calcium per 8 ounces.',
          '⚠ You must certify that this product contains no less than the required minimum 276mg total calcium per 8 ounces.',
        ],
        [
          'Protein',
          'Protein (g) per 8 ounces',
          4,
          8,
          'Min 8g protein per 8 ounces',
          '⚠ Protein (g) per 8 ounces is below the required 8g minimum.',
          'I certify that this product contains at least 8g protein per 8 ounces.',
          '⚠ You must certify that this product contains no less than the required minimum 8g total protein allowed per 8 ounces.',
        ],
        [
          'VitaminA',
          'Vitamin A (mcg) per 8 ounces',
          5,
          150,
          'Min 150mcg vitamin A per 8 ounces',
          '⚠ Vitamin A (mcg) per 8 ounces is below the required 150mcg minimum.',
          'I certify that this product contains at least 150mcg vitamin A per 8 ounces.',
          '⚠ You must certify that this product contains no less than the required minimum 150mcg total vitamin A per 8 ounces.',
        ],
        [
          'VitaminD',
          'Vitamin D (mcg) per 8 ounces',
          4,
          2.5,
          'Min 2.5 mcg vitamin D per 8 ounces',
          '⚠ Vitamin D (mcg) per 8 ounces is below the required 2.5mcg minimum.',
          'I certify that this product contains at least 2.5mcg vitamin D per 8 ounces.',
          '⚠ You must certify that this product contains no less than the required minimum 2.5mcg total vitamin D per 8 ounces.',
        ],
        [
          'VitaminB12',
          'Vitamin B12 (mcg) per 8 ounces',
          3,
          1.1,
          'Min 1.1 mcg vitamin B12 per 8 ounces',
          '⚠ Vitamin B12 (mcg) per 8 ounces is below the required 1.1mcg minimum.',
          'I certify that this product contains at least 1.1mcg vitamin B12 per 8 ounces.',
          '⚠ You must certify that this product contains no less than the required minimum 1.1mcg total vitamin B12 per 8 ounces.',
        ],
        [
          'Magnesium',
          'Magnesium (mg) per 8 ounces',
          5,
          24,
          'Min 24mg magnesium per 8 ounces',
          '⚠ Magnesium (mg) per 8 ounces is below the required 24mg minimum.',
          'I certify that this product contains at least 24mg magnesium per 8 ounces.',
          '⚠ You must certify that this product contains no less than the required minimum 24mg total magnesium per 8 ounces.',
        ],
        [
          'Phosphorus',
          'Phosphorus (mg) per 8 ounces',
          5,
          222,
          'Min 222mg phosphorus per 8 ounces',
          '⚠ Phosphorus (mg) per 8 ounces is below the required 222mg minimum.',
          'I certify that this product contains at least 222mg phosphorus per 8 ounces.',
          '⚠ You must certify that this product contains no less than the required minimum 222mg total phosphorus per 8 ounces.',
        ],
        [
          'Potassium',
          'Potassium (mg) per 8 ounces',
          5,
          349,
          'Min 349mg potassium per 8 ounces',
          '⚠ Potassium (mg) per 8 ounces is below the required 349mg minimum.',
          'I certify that this product contains at least 349mg potassium per 8 ounces.',
          '⚠ You must certify that this product contains no less than the required minimum 349mg total potassium per 8 ounces.',
        ],
        [
          'Riboflavin',
          'Riboflavin (mg) per 8 ounces',
          5,
          0.44,
          'Min 0.44mg riboflavin per 8 ounces',
          '⚠ Riboflavin (mg) per 8 ounces is below the required 0.44mg minimum.',
          'I certify that this product contains at least 0.44mg riboflavin per 8 ounces.',
          '⚠ You must certify that this product contains no less than the required minimum 0.44mg total riboflavin per 8 ounces.',
        ],
      ].map(
        ([
          field,
          label,
          maxLength,
          min,
          placeholder,
          error,
          certStatement,
          certError,
          max,
          maxMessage,
        ]) => (
          <Row key={field}>
            <Col xs={6}>
              {renderField(
                field,
                label,
                maxLength,
                min,
                placeholder,
                error,
                max,
                maxMessage
              )}
            </Col>
            <Col xs={6}>
              {renderSwitch(`${field}Certification`, certStatement, certError)}
            </Col>
          </Row>
        )
      )}
    </>
  )
}

PlantMilkNutritionReview.propTypes = {
  item: PropTypes.object,
  isDisabled: PropTypes.bool,
}

export default PlantMilkNutritionReview
